/**
 * Rotates an image by a specified angle in degrees
 * and trims all white margins around it.
 */
export const rotateImage = (
    base64Image: string,
    degrees: number,
    mimeType: string = "image/png"
): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
            try {
                const w = img.naturalWidth || img.width;
                const h = img.naturalHeight || img.height;

                if (!w || !h) {
                    reject(new Error("Image has zero width/height"));
                    return;
                }

                const norm = ((degrees % 360) + 360) % 360;
                const rad = (norm * Math.PI) / 180;

                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    reject(new Error("Could not get canvas context"));
                    return;
                }

                if (norm === 0 || norm === 180) {
                    canvas.width = w;
                    canvas.height = h;
                } else if (norm === 90 || norm === 270) {
                    canvas.width = h;
                    canvas.height = w;
                } else {
                    const absCos = Math.abs(Math.cos(rad));
                    const absSin = Math.abs(Math.sin(rad));
                    canvas.width = Math.ceil(w * absCos + h * absSin);
                    canvas.height = Math.ceil(w * absSin + h * absCos);
                }

                // white background
                ctx.fillStyle = "#ffffff";
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                ctx.translate(canvas.width / 2, canvas.height / 2);
                ctx.rotate(rad);
                ctx.drawImage(img, -w / 2, -h / 2);


                // ---- TRIM WHITE SPACE ----
                console.log("Starting trim operation...");
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const { data, width, height } = imageData;

                let top = height;
                let left = width;
                let right = 0;
                let bottom = 0;

                // Log top-left pixel to check white perception
                console.log(`Pixel (0,0): R=${data[0]}, G=${data[1]}, B=${data[2]}, A=${data[3]}`);

                // Define background as White OR Transparent
                // Relaxed tolerance to 230 to catch compression artifacts
                const isBackground = (i: number) => {
                    const r = data[i];
                    const g = data[i + 1];
                    const b = data[i + 2];
                    const a = data[i + 3];

                    // Fully transparent or very low alpha is background
                    if (a < 50) return true;

                    // "White" is high RGB values
                    return r > 230 && g > 230 && b > 230;
                };

                for (let y = 0; y < height; y++) {
                    for (let x = 0; x < width; x++) {
                        const i = (y * width + x) * 4;
                        if (!isBackground(i)) {
                            if (x < left) left = x;
                            if (x > right) right = x;
                            if (y < top) top = y;
                            if (y > bottom) bottom = y;
                        }
                    }
                }

                console.log(`Trim Bounding Box: Top=${top}, Bottom=${bottom}, Left=${left}, Right=${right}, Width=${width}, Height=${height}`);

                if (left > right || top > bottom) {
                    // image is fully white
                    console.warn("Trim aborted: Image identified as fully white/empty. Return original.");
                    resolve(canvas.toDataURL(mimeType));
                    return;
                }

                // Check if bounds matched full size
                if (left === 0 && top === 0 && right === width - 1 && bottom === height - 1) {
                    console.warn("Trim matched full image size (corners were not white?). Return original.");
                    // We can verify corners specifically here if we want detailed debugging
                    const idx0 = 0;
                    console.log(`TopLeft Check: IsBackground=${isBackground(idx0)}, Data=[${data[0]},${data[1]},${data[2]},${data[3]}]`);
                }

                const trimWidth = right - left + 1;
                const trimHeight = bottom - top + 1;

                const trimmedCanvas = document.createElement("canvas");
                trimmedCanvas.width = trimWidth;
                trimmedCanvas.height = trimHeight;

                const trimmedCtx = trimmedCanvas.getContext("2d");
                if (!trimmedCtx) {
                    reject(new Error("Could not get trimmed canvas context"));
                    return;
                }

                trimmedCtx.drawImage(
                    canvas,
                    left,
                    top,
                    trimWidth,
                    trimHeight,
                    0,
                    0,
                    trimWidth,
                    trimHeight
                );
                console.log("Image trimmed successfully");
                console.log(trimmedCanvas.toDataURL(mimeType));
                resolve(trimmedCanvas.toDataURL(mimeType));
            } catch (e) {
                reject(e);
            }
        };

        img.onerror = reject;

        img.src = base64Image.startsWith("data:")
            ? base64Image
            : `data:${mimeType};base64,${base64Image}`;
    });
};